import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ErrorNotice, Icon } from '@hive/ui';
import { api, errorMessage, type Screen } from '../api/client';
import { useLiveScreens, useVenues } from '../api/hooks';
import { VenueHeader } from '../components/VenueHeader';

/** Uppercase, strip anything that is not part of a code. Matches the server's normalisation. */
export function normalizeCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
}

export function PairPage() {
  const [params] = useSearchParams();
  const { venues } = useVenues();
  const venueId = params.get('venue') ?? venues?.find((v) => v.source === 'real')?.id ?? venues?.[0]?.id;
  const venue = venues?.find((v) => v.id === venueId);
  const { screens } = useLiveScreens(venueId);

  const [code, setCode] = useState(() => normalizeCode(params.get('code') ?? ''));
  const [screenId, setScreenId] = useState(params.get('screen') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paired, setPaired] = useState<Screen | null>(null);

  // Default to the first screen without a player.
  useEffect(() => {
    if (!screenId && screens?.length) setScreenId((screens.find((s) => s.status === 'unpaired') ?? screens[0]).id);
  }, [screens, screenId]);

  const target = screens?.find((s) => s.id === screenId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data, error } = await api.POST('/v1/pairing/claim', { body: { code, screenId } });
      if (data) setPaired(data);
      else setError(errorMessage(error, 'That code did not work. Check the code on the TV and try again.'));
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="hv-root k-page">
      {venue && <VenueHeader name={venue.name} context="Pair a new TV screen" />}
      <section className="k-card k-pair">
        <div className="k-pair__head">
          <span aria-hidden="true" className="k-icon-round">
            <Icon name="pair" size={20} />
          </span>
          <div>
            <h1 className="k-pair__title">{paired ? `Paired to ${paired.name.toLowerCase()}` : 'Pair a new TV screen'}</h1>
            <p className="hv-muted">
              {paired ? 'The TV switches over in a moment.' : 'Enter the code shown on the TV, then choose which screen it is.'}
            </p>
          </div>
        </div>
        {paired ? (
          <div className="k-row">
            <Link className="hv-btn hv-btn--primary hv-btn--lg" to={`/screens/${paired.id}/change?venue=${paired.venueId}`}>
              Choose what plays
            </Link>
            <Link className="hv-btn hv-btn--secondary hv-btn--lg" to="/">
              Back to screens
            </Link>
          </div>
        ) : (
          <form className="k-form" onSubmit={submit}>
            {error && <ErrorNotice title="That code did not work">{error}</ErrorNotice>}
            <div className="hv-field">
              <label className="hv-field__label" htmlFor="code">
                Pairing code
              </label>
              <input
                id="code"
                className="hv-input k-code-input"
                value={code}
                onChange={(e) => setCode(normalizeCode(e.target.value))}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                placeholder="ABC234"
                aria-describedby="code-help"
                required
                minLength={6}
              />
              <small id="code-help" className="hv-muted k-small">
                Six letters and numbers. The code on the TV refreshes every ten minutes.
              </small>
            </div>
            <div className="hv-field">
              <label className="hv-field__label" htmlFor="screen">
                Screen
              </label>
              <select id="screen" className="hv-input" value={screenId} onChange={(e) => setScreenId(e.target.value)} required>
                {screens?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.code ? ` · ${s.code}` : ''}
                    {s.status !== 'unpaired' ? ' (has a player)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {target && target.status !== 'unpaired' && <p className="k-inline-warn">This screen already has a player. Pairing replaces it.</p>}
            <div className="k-row">
              <button className="hv-btn hv-btn--primary hv-btn--lg" type="submit" disabled={busy || code.length < 6 || !screenId}>
                {busy ? 'Pairing…' : 'Pair screen'}
              </button>
              <Link className="hv-btn hv-btn--secondary hv-btn--lg" to="/">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
